// Shared UI primitives for the Recipients step accordions.

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import type { ReactNode } from "react";
import { defaultScheduleDate } from "@/utils/scheduleDate-helper";

// ─── LockedSection ─────────────────────────────────────────────────────────────
//
// Field-level lock for ONE Step-2 accordion. A `<fieldset disabled>` is the load-bearing element,
// not styling: `pointer-events: none` stops the mouse only, leaving every descendant control in the
// tab order and activatable by keyboard (Space/Enter on an sr-only radio or a hover-only delete
// button would still mutate for real). A disabled fieldset natively disables and un-tabs every
// descendant — the platform primitive for exactly this. `minInlineSize: 0` keeps the fieldset from
// refusing to shrink below its content. The explanatory note sits OUTSIDE the dimmed layer so it
// stays fully legible.

const FIELDSET_RESET = { border: 0, margin: 0, padding: 0, minInlineSize: 0 } as const;

export function LockedSection({
  locked,
  reason,
  children,
}: {
  locked: boolean;
  reason?: string;
  children: ReactNode;
}) {
  return (
    <div>
      {locked && reason && (
        <div
          className="flex items-center gap-2 mb-2 px-3 py-2 rounded-lg"
          style={{ background: "var(--color-light-dust)", border: "1px solid var(--color-smoke)" }}
        >
          <LockOutlinedIcon sx={{ fontSize: 14, color: "var(--color-dark-dust)" }} />
          <p className="font-lato text-[12px] text-graphite leading-none m-0">{reason}</p>
        </div>
      )}
      <fieldset
        disabled={locked}
        style={
          locked ? { ...FIELDSET_RESET, opacity: 0.55, pointerEvents: "none" } : FIELDSET_RESET
        }
      >
        {children}
      </fieldset>
    </div>
  );
}

// ─── RadioOption ─────────────────────────────────────────────────────────────

export interface RadioOptionProps {
  selected: boolean;
  onSelect: () => void;
  label: string;
  hint?: string;
  recommended?: boolean;
  pro?: boolean;
  /** Not available yet — dim the option and block selection. */
  comingSoon?: boolean;
  /** Small note rendered next to the label when locked, e.g. "Coming soon in October 2026". */
  comingSoonNote?: string;
  hasVisiblePanel?: boolean;
}

export function RadioOption({
  selected,
  onSelect,
  label,
  hint,
  recommended,
  pro,
  comingSoon = false,
  comingSoonNote,
  hasVisiblePanel = false,
}: RadioOptionProps) {
  const merging = selected && hasVisiblePanel;
  const active = selected && !comingSoon;
  // Dim the option (radio + text) so it reads as unavailable — no blur, the copy stays readable.
  const dim = comingSoon ? "opacity-55 select-none" : "";

  return (
    <label
      className={`relative flex items-start gap-3 px-4 py-3.5 transition-all border ${
        merging ? "rounded-t-btn rounded-b-none border-b-transparent" : "rounded-btn"
      } ${
        comingSoon
          ? "cursor-not-allowed border-card-border bg-white"
          : `cursor-pointer hover:border-rodeo-dust hover:bg-clear-dust ${
              selected ? "border-rodeo-black bg-clear-dust" : "border-card-border bg-white"
            }`
      }`}
    >
      <input
        type="radio"
        className="sr-only"
        checked={active}
        onChange={onSelect}
        disabled={comingSoon}
      />

      <div
        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-px transition-colors ${dim}`}
        style={{ borderColor: active ? "var(--color-rodeo-black)" : "var(--color-smoke)" }}
        aria-hidden="true"
      >
        <div
          className="w-2.5 h-2.5 rounded-full bg-rodeo-black transition-transform duration-150"
          style={{ transform: active ? "scale(1)" : "scale(0)" }}
        />
      </div>

      <div className={`flex-1 min-w-0 ${dim}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-lato text-[14px] font-semibold text-rodeo-black">{label}</span>
          {comingSoon && comingSoonNote && (
            <span className="font-lato text-[12px] text-graphite">{comingSoonNote}</span>
          )}
          {recommended && (
            <span
              className="inline-block px-2 py-px rounded-pill font-lato text-[10px] font-bold uppercase tracking-[1px]"
              style={{ background: "var(--color-good-sage-5)", color: "var(--color-good-sage)" }}
            >
              Recommended
            </span>
          )}
          {pro && (
            <span
              className="inline-flex items-center gap-1 px-2 rounded-pill font-lato text-[9px] font-bold uppercase tracking-[1.2px] bg-white border"
              style={{
                color: "var(--color-good-ochre)",
                borderColor: "var(--color-good-ochre-border)",
                padding: "3px 8px",
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 11 }} />
              PRO
            </span>
          )}
        </div>
        {hint && <p className="font-lato text-[12px] text-graphite mt-1 leading-relaxed">{hint}</p>}
      </div>
    </label>
  );
}

// ─── ConditionalPanel ────────────────────────────────────────────────────────
//
// merged=false (default): standalone wax panel with left accent — used inside GiftReminders.
// merged=true: seamless extension of the selected radio above — used in Schedule and Expiration.

export function ConditionalPanel({
  visible,
  children,
  merged = false,
}: {
  visible: boolean;
  children: React.ReactNode;
  merged?: boolean;
}) {
  return (
    <div
      className={`grid duration-300 ease-in-out ${
        merged ? "transition-[grid-template-rows,margin-top]" : "transition-[grid-template-rows]"
      }`}
      style={{
        gridTemplateRows: visible ? "1fr" : "0fr",
        marginTop: merged ? (visible ? "-8px" : "0px") : undefined,
      }}
    >
      <div className="overflow-hidden">
        <div
          className={`transition-opacity duration-200 ${visible ? "opacity-100" : "opacity-0"} ${
            merged ? "" : "mt-2"
          }`}
        >
          {merged ? (
            <div
              style={{
                background: "var(--color-clear-dust)",
                border: "1px solid var(--color-rodeo-black)",
                borderTop: "none",
                borderRadius: "0 0 var(--radius-btn) var(--radius-btn)",
                padding: "16px",
              }}
            >
              {children}
            </div>
          ) : (
            <div
              className="px-4 py-4 rounded-btn"
              style={{
                background: "var(--color-wax)",
                borderLeft: "3px solid var(--color-rodeo-dust)",
              }}
            >
              {children}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── ReminderActionButtons ───────────────────────────────────────────────────

export function ReminderActionButtons({
  onEdit,
  onPreview,
}: {
  onEdit: () => void;
  onPreview: () => void;
}) {
  const btnClass =
    "inline-flex items-center gap-1 px-2.5 py-1.5 font-lato text-[11px] font-semibold uppercase tracking-[1px] text-graphite transition-all border border-smoke bg-white rounded-sm hover:border-dark-dust hover:text-dark-dust hover:bg-clear-dust";

  return (
    <div className="flex items-center gap-1.5 ml-auto shrink-0">
      <button type="button" title="Edit reminder email" onClick={onEdit} className={btnClass}>
        <EditOutlinedIcon sx={{ fontSize: 14 }} />
        Edit
      </button>
      <button type="button" title="Preview reminder email" onClick={onPreview} className={btnClass}>
        <VisibilityOutlinedIcon sx={{ fontSize: 14 }} />
        Preview
      </button>
    </div>
  );
}

export function StyledInput({
  type,
  min,
  style,
  ...rest
}: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      type={type}
      min={type === "date" ? (min ?? defaultScheduleDate()) : min}
      {...rest}
      className="font-lato text-[14px] text-rodeo-black px-3 py-2 rounded-btn outline-none transition-colors"
      style={{
        border: "1px solid var(--color-smoke)",
        background: "var(--color-white)",
        ...style,
      }}
    />
  );
}

export function StyledSelect(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...props}
      className="font-lato text-[14px] text-rodeo-black px-3 py-2 rounded-btn outline-none transition-colors bg-white"
      style={{ border: "1px solid var(--color-smoke)", ...props.style }}
    />
  );
}
